import React, { useState, useEffect } from 'react';
import { formatInTenantTz } from '../../../lib/datetime';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { ScrollArea } from '../../ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { useToast } from '../../../hooks/use-toast';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useBusiness } from '../../../contexts/BusinessContext';
import {
  Bot,
  Phone,
  PhoneCall,
  User,
  Brain,
  AlertTriangle,
  CheckCircle,
  Clock,
  MessageSquare,
  Target
} from 'lucide-react';

const AIVoiceMonitor = ({ businessId }) => {
  const { toast } = useToast();
  const { makeAuthenticatedRequest } = useCRMAuth();
  const { currentBusiness } = useBusiness();
  const bizId = businessId || currentBusiness?.brandId;
  const [activeCalls, setActiveCalls] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [showEscalationDialog, setShowEscalationDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  // Phase 5 — REAL AI voice sessions from db.voice_call_sessions (tenant-scoped). No placeholders:
  // active_sessions = genuinely in-progress; recent_sessions = stored history; honest empty if none.
  useEffect(() => {
    if (!bizId) { setActiveCalls([]); setRecentSessions([]); return; }
    let cancelled = false;
    const fetchSessions = async () => {
      try {
        const resp = await makeAuthenticatedRequest(`/api/v1/ai-agent/voice-sessions/${encodeURIComponent(bizId)}`);
        if (!resp.ok) { if (!cancelled) { setActiveCalls([]); setRecentSessions([]); } return; }
        const d = await resp.json();
        if (!cancelled) {
          setActiveCalls(Array.isArray(d.active_sessions) ? d.active_sessions : []);
          setRecentSessions(Array.isArray(d.recent_sessions) ? d.recent_sessions : []);
        }
      } catch (e) {
        if (!cancelled) { setActiveCalls([]); setRecentSessions([]); }
      }
    };
    fetchSessions();
    const interval = setInterval(fetchSessions, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [bizId, makeAuthenticatedRequest]);

  const handleTakeoverCall = async (call) => {
    setLoading(true);
    try {
      const response = await makeAuthenticatedRequest('/api/v1/ai-voice/escalate', {
        method: 'POST',
        body: JSON.stringify({
          session_id: call.session_id,
          escalation_reason: 'Operator takeover from AI Voice Agent',
          escalate_to: 'operator'
        })
      });

      if (!response.ok) throw new Error('Failed to escalate call');

      toast({
        title: "Call Taken Over",
        description: `Call from ${call.customer_phone} has been transferred to you.`,
      });

      setActiveCalls(prev => prev.filter(c => c.session_id !== call.session_id));
    } catch (error) {
      console.error('Error taking over call:', error);
      toast({
        title: "Takeover Failed",
        description: "Could not take over the call. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (call) => {
    setSelectedCall(call);
    setShowEscalationDialog(true);
  };

  const getIntentBadgeColor = (intent) => {
    const colors = {
      'inquiry': 'bg-blue-500',
      'complaint': 'bg-red-500',
      'support_request': 'bg-yellow-500',
      'information_request': 'bg-green-500',
      'escalation_request': 'bg-purple-500',
      'unknown': 'bg-gray-500'
    };
    return colors[intent] || 'bg-gray-500';
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'initiated': 'bg-blue-500',
      'in_progress': 'bg-green-500',
      'resolved': 'bg-gray-500',
      'escalated': 'bg-purple-500',
      'ended': 'bg-gray-400'
    };
    return colors[status] || 'bg-gray-500';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return formatInTenantTz(date, { hour: '2-digit', minute: '2-digit' });
  };

  const calculateDuration = (startedAt) => {
    const start = new Date(startedAt);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000);
    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active AI Calls</p>
                <p className="text-2xl font-bold">{activeCalls.length}</p>
              </div>
              <Bot className="h-8 w-8 text-blue-500" />
            </div>
            <Badge variant="outline" className="mt-2 text-xs" data-testid="crm-auth-status">
              {makeAuthenticatedRequest ? <CheckCircle className="w-3 h-3 mr-1 text-green-500" /> : <AlertTriangle className="w-3 h-3 mr-1 text-red-500" />}
              CRM Auth
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                {/* No live voice auto-resolution metric endpoint yet. real wiring lands in the AI backend phase */}
                <p className="text-sm text-gray-500">Auto-Resolved</p>
                <p className="text-2xl font-bold">—</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                {/* No live voice escalation metric endpoint yet. real wiring lands in the AI backend phase */}
                <p className="text-sm text-gray-500">Escalated</p>
                <p className="text-2xl font-bold">—</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                {/* No live voice confidence metric endpoint yet. real wiring lands in the AI backend phase */}
                <p className="text-sm text-gray-500">Avg Confidence</p>
                <p className="text-2xl font-bold">—</p>
              </div>
              <Brain className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active AI Calls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PhoneCall className="h-5 w-5" />
            Active AI Voice Agent Calls
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeCalls.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Bot className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-semibold mb-2">No Active AI Calls</p>
              {window.location.pathname.startsWith('/customer') ? (
                <p className="text-sm">
                  Active voice calls with businesses will appear here when in progress.
                </p>
              ) : (
                <>
                  <p className="text-sm mb-4">
                    Use the AI Voice Simulator to create test calls or wait for incoming customer calls
                  </p>
                  <button
                    onClick={() => { window.location.href = '/business/ai-voice-simulator'; }}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Go to AI Voice Simulator →
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {activeCalls.map((call) => (
                <Card key={call.session_id} className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-3 flex-1">
                        <div className="flex items-center gap-3">
                          <User className="h-5 w-5 text-gray-500" />
                          <div>
                            <p className="font-semibold">
                              {call.customer_name || call.customer_phone}
                            </p>
                            <p className="text-sm text-gray-500">{call.customer_phone}</p>
                          </div>
                          <Badge className={getStatusBadgeColor(call.status)}>
                            {call.status}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-4 text-sm">
                          {call.detected_intent && (
                            <div className="flex items-center gap-2">
                              <Badge className={getIntentBadgeColor(call.detected_intent)}>
                                {call.detected_intent}
                              </Badge>
                              {typeof call.intent_confidence === 'number' && (
                                <span className="text-gray-500">
                                  {Math.round(call.intent_confidence * 100)}% confidence
                                </span>
                              )}
                            </div>
                          )}
                          {call.detected_language && (
                            <div className="flex items-center gap-1">
                              <MessageSquare className="h-4 w-4 text-gray-500" />
                              <span className="text-gray-500">{call.detected_language}</span>
                            </div>
                          )}
                          {call.started_at && (
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4 text-gray-500" />
                              <span className="text-gray-500">{calculateDuration(call.started_at)}</span>
                            </div>
                          )}
                        </div>

                        {Array.isArray(call.conversation_history) && call.conversation_history.length > 0 && (
                          <div className="bg-gray-50 p-3 rounded text-sm">
                            <p className="text-gray-500 text-xs mb-1">Latest exchange:</p>
                            {call.conversation_history.slice(-2).map((turn, idx) => (
                              <p key={idx} className="mb-1">
                                <span className="font-semibold">
                                  {turn.speaker === 'ai_agent' ? '🤖 AI' : '👤 Customer'}:
                                </span>{' '}
                                {turn.text}
                              </p>
                            ))}
                          </div>
                        )}

                        {call.solution_matched && (
                          <div className="flex items-center gap-2 text-sm text-green-600">
                            <Target className="h-4 w-4" />
                            <span>Solution matched from Solution Bank</span>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button size="sm" onClick={() => handleViewDetails(call)} variant="outline">
                          View Details
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleTakeoverCall(call)}
                          disabled={loading}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Phone className="h-4 w-4 mr-1" />
                          Take Over
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Voice Sessions (real, from db.voice_call_sessions) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Recent Voice Sessions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentSessions.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No recorded voice sessions yet.
            </div>
          ) : (
            <div className="divide-y">
              {recentSessions.map((call) => (
                <button
                  key={call.session_id}
                  onClick={() => handleViewDetails(call)}
                  className="w-full text-left flex items-center justify-between py-3 hover:bg-gray-50 px-2 rounded"
                >
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium">
                        {call.customer_name || call.customer_phone || 'Unknown caller'}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {call.detected_intent && <span>{call.detected_intent}</span>}
                        {call.started_at && <span>{formatTimestamp(call.started_at)}</span>}
                      </div>
                    </div>
                  </div>
                  <Badge className={getStatusBadgeColor(call.status)}>{call.status}</Badge>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Call Details Dialog */}
      <Dialog open={showEscalationDialog} onOpenChange={setShowEscalationDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>AI Call Details</DialogTitle>
            <DialogDescription>Full conversation history and analysis</DialogDescription>
          </DialogHeader>

          {selectedCall && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Session reference</p>
                  {/* D3: short friendly reference, never the full raw session id */}
                  <p className="font-mono">{selectedCall.session_id ? String(selectedCall.session_id).slice(-8).toUpperCase() : '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Customer</p>
                  <p>{selectedCall.customer_name || selectedCall.customer_phone}</p>
                </div>
                <div>
                  <p className="text-gray-500">Language</p>
                  <Badge>{selectedCall.detected_language || '—'}</Badge>
                </div>
                <div>
                  <p className="text-gray-500">Intent</p>
                  <Badge className={getIntentBadgeColor(selectedCall.detected_intent)}>
                    {selectedCall.detected_intent || 'unknown'}
                    {typeof selectedCall.intent_confidence === 'number'
                      ? ` (${Math.round(selectedCall.intent_confidence * 100)}%)`
                      : ''}
                  </Badge>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Conversation History
                </h3>
                <ScrollArea className="h-64 border rounded p-4 bg-gray-50">
                  <div className="space-y-3">
                    {(!Array.isArray(selectedCall.conversation_history) || selectedCall.conversation_history.length === 0) && (
                      <p className="text-sm text-gray-500">No conversation transcript recorded for this session.</p>
                    )}
                    {(Array.isArray(selectedCall.conversation_history) ? selectedCall.conversation_history : []).map((turn) => (
                      <div
                        key={turn.turn_id}
                        className={`p-3 rounded ${
                          turn.speaker === 'ai_agent'
                            ? 'bg-blue-100 border-l-4 border-l-blue-500'
                            : 'bg-green-100 border-l-4 border-l-green-500'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-sm">
                            {turn.speaker === 'ai_agent' ? '🤖 AI Agent' : '👤 Customer'}
                          </span>
                          <span className="text-xs text-gray-500">{formatTimestamp(turn.timestamp)}</span>
                        </div>
                        <p className="text-sm">{turn.text}</p>
                        {turn.intent && (
                          <div className="mt-2 text-xs text-gray-600">
                            Intent: {turn.intent} ({Math.round(turn.confidence * 100)}%)
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowEscalationDialog(false)}>
                  Close
                </Button>
                <Button
                  onClick={() => {
                    handleTakeoverCall(selectedCall);
                    setShowEscalationDialog(false);
                  }}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Phone className="h-4 w-4 mr-1" />
                  Take Over Call
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIVoiceMonitor;